<script lang="ts">
  // Shared sparkline. `values` are raw (self-normalised for the y-position);
  // hovering shows a marker + a fixed tooltip with the point's label (e.g. a
  // date) and its formatted value. Colour comes from `currentColor` (set a text
  // colour via the `class` prop), height/width from the caller's classes.
  let {
    values,
    labels = null,
    format = (v: number) => String(v),
    class: cls = '',
  }: {
    values: number[];
    labels?: (string | number)[] | null;
    format?: (v: number) => string;
    class?: string;
  } = $props();

  const n = $derived(values.length);
  const min = $derived(values.length ? Math.min(...values) : 0);
  const max = $derived(values.length ? Math.max(...values) : 0);
  const norm = (x: number): number => (max > min ? (x - min) / (max - min) : 0.5);
  // Polyline points in a 0..100 viewBox, padded to 6..94 so the line/markers
  // don't clip. Y position in %; x evenly spaced.
  const px = (i: number): number => (n > 1 ? (i / (n - 1)) * 100 : 50);
  const py = (v: number): number => (1 - norm(v)) * 88 + 6;
  const points = $derived(values.map((v, i) => `${px(i).toFixed(2)},${py(v).toFixed(2)}`).join(' '));

  let el = $state<HTMLDivElement>();
  let hoverI = $state<number | null>(null);
  let tipX = $state(0);
  let tipY = $state(0);

  function move(e: MouseEvent): void {
    if (!el || n < 2) return;
    const r = el.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    hoverI = Math.round(frac * (n - 1));
    tipX = r.left + px(hoverI) * 0.01 * r.width;
    tipY = r.top;
  }
</script>

<div
  bind:this={el}
  class="relative {cls}"
  role="presentation"
  onmousemove={move}
  onmouseleave={() => (hoverI = null)}
>
  <svg class="block h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
    <polyline
      {points}
      fill="none"
      stroke="currentColor"
      stroke-width="1.75"
      vector-effect="non-scaling-stroke"
      stroke-linejoin="round"
      stroke-linecap="round"
      opacity="0.85"
    />
  </svg>
  {#if hoverI != null}
    <div class="pointer-events-none absolute inset-y-0 w-px bg-current opacity-25" style="left:{px(hoverI)}%"></div>
    <div
      class="pointer-events-none absolute h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-current"
      style="left:{px(hoverI)}%; top:{py(values[hoverI] ?? 0)}%"
    ></div>
  {/if}
</div>

{#if hoverI != null}
  <div
    class="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-full rounded border hair bg-base-100 px-1.5 py-1 text-center font-mono text-[0.6rem] leading-tight shadow-lg"
    style="left:{tipX}px; top:{tipY - 4}px;"
    role="tooltip"
  >
    <div class="font-semibold text-base-content/85">{format(values[hoverI] ?? 0)}</div>
    {#if labels && labels[hoverI] != null}<div class="text-base-content/65">{labels[hoverI]}</div>{/if}
  </div>
{/if}
